import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";

/**
 * Calm Completion Effect
 * 
 * A refined, premium completion experience:
 * - Flame leads slightly (progress signal)
 * - Water-like ripple follows
 * - Particles drift outward
 * - Heartbeat double-thump sound
 * 
 * Sequence: flame → ripple → particles → resolve
 */

// Heartbeat double-thump - two distinct thumps
// Note: This is a backup/fallback - main sound is in utils/sounds.js
const createHeartbeatSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playThump = (time, volume = 0.2, pitch = 60) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Low, warm frequency for heartbeat feel
      oscillator.frequency.setValueAtTime(pitch, time);
      oscillator.frequency.exponentialRampToValueAtTime(pitch * 0.6, time + 0.1);
      oscillator.type = 'sine';
      
      // Quick attack, natural decay
      gainNode.gain.setValueAtTime(0, time);
      gainNode.gain.linearRampToValueAtTime(volume, time + 0.015);
      gainNode.gain.exponentialRampToValueAtTime(0.001, time + 0.15);
      
      oscillator.start(time);
      oscillator.stop(time + 0.18);
    };
    
    const now = audioContext.currentTime;
    
    // First thump - stronger, deeper
    playThump(now, 0.22, 55);
    
    // Second thump - softer, slightly higher, 150ms later
    playThump(now + 0.15, 0.15, 65);
    
  } catch (e) {
    // Audio not supported, fail silently
  }
};

// Generate calm particles (14-18) that float up toward the flame
// Plus 5 extra tail-end particles for a finishing flourish
// Plus 8 side-squirt particles that shoot left/right then float up
const generateCalmParticles = () => {
  const count = Math.floor(Math.random() * 5) + 14; // 14-18 particles (was 8-12)
  const mainParticles = Array.from({ length: count }, (_, i) => {
    // Determine color - mix of warm and aqua
    const colorRoll = Math.random();
    let color;
    if (colorRoll < 0.25) {
      // Aqua colors (25% of particles)
      color = `rgba(61, 196, 208, ${0.5 + Math.random() * 0.2})`; // Aqua/teal
    } else if (colorRoll < 0.5) {
      // Soft aqua-cyan
      color = `rgba(12, 188, 191, ${0.45 + Math.random() * 0.15})`; // Cyan
    } else if (colorRoll < 0.75) {
      // Soft orange
      color = `rgba(245, 130, 39, ${0.5 + Math.random() * 0.15})`;
    } else {
      // Warm amber
      color = `rgba(251, 191, 36, ${0.4 + Math.random() * 0.15})`;
    }
    
    return {
      id: i,
      type: 'main',
      // Start from center area
      startX: 42 + Math.random() * 16,
      startY: 42 + Math.random() * 16,
      // Float upward all the way to top ribbon
      endX: 45 + Math.random() * 45, // Spread across toward flame area
      endY: -280 - Math.random() * 120, // Float all the way up into header ribbon
      // Larger dots (+10%)
      size: 6.6 + Math.random() * 3.85,
      // Stagger after ripple (200-400ms)
      delay: 0.2 + (i * 0.02), // Tighter stagger for more particles
      color,
    };
  });
  
  // Add 5 tail-end particles with longer delay for finishing flourish (was 3)
  const tailParticles = Array.from({ length: 5 }, (_, i) => {
    const colorRoll = Math.random();
    let color;
    if (colorRoll < 0.5) {
      color = `rgba(61, 196, 208, ${0.55 + Math.random() * 0.2})`; // Aqua
    } else {
      color = `rgba(245, 130, 39, ${0.55 + Math.random() * 0.15})`; // Orange
    }
    
    return {
      id: count + i,
      type: 'tail',
      startX: 44 + Math.random() * 12,
      startY: 44 + Math.random() * 12,
      endX: 50 + Math.random() * 35,
      endY: -300 - Math.random() * 80,
      // Tail particles (+10%)
      size: 5.5 + Math.random() * 3.3,
      // Tail particles start later (500-700ms delay)
      delay: 0.5 + (i * 0.06),
      color,
    };
  });
  
  // Add 8 side-squirt particles - 4 left, 4 right
  const sideParticles = Array.from({ length: 8 }, (_, i) => {
    const isLeft = i < 4;
    const colorRoll = Math.random();
    let color;
    if (colorRoll < 0.4) {
      color = `rgba(61, 196, 208, ${0.55 + Math.random() * 0.2})`; // Aqua
    } else if (colorRoll < 0.7) {
      color = `rgba(245, 130, 39, ${0.55 + Math.random() * 0.15})`; // Orange
    } else {
      color = `rgba(251, 191, 36, ${0.5 + Math.random() * 0.15})`; // Amber
    }
    
    return {
      id: count + 5 + i,
      type: 'side',
      isLeft,
      startX: 50, // Start from center
      startY: 50,
      // Squirt sideways first, then curve up
      midX: isLeft ? (15 + Math.random() * 15) : (70 + Math.random() * 15), // Left or right
      midY: 45 + Math.random() * 20, // Slight vertical variation
      endX: isLeft ? (25 + Math.random() * 20) : (55 + Math.random() * 20), // Converge back toward center-ish
      endY: -260 - Math.random() * 100, // Float up to flame
      size: 5.5 + Math.random() * 3,
      delay: 0.05 + (i * 0.03), // Quick burst at start
      color,
    };
  });
  
  return [...mainParticles, ...tailParticles, ...sideParticles];
};

export const CompletionCelebration = ({ 
  isActive, 
  onComplete,
  origin, // {x, y} pixel position of completed task
}) => {
  const [particles, setParticles] = useState([]);
  const soundPlayedRef = useRef(false);

  useEffect(() => {
    if (isActive) {
      setParticles(generateCalmParticles());
      
      // Sound is handled by playHeartbeatSound in utils/sounds.js
      // This component only handles visuals
      soundPlayedRef.current = true;
      
      // Total duration ~1900ms for visuals
      const timer = setTimeout(() => {
        onComplete?.();
        soundPlayedRef.current = false;
      }, 1900);
      
      return () => clearTimeout(timer);
    } else {
      soundPlayedRef.current = false;
    }
  }, [isActive, onComplete]);

  if (!isActive) return null;
  
  // Calculate origin position as percentages, fallback to center
  const originX = origin ? (origin.x / window.innerWidth) * 100 : 50;
  const originY = origin ? (origin.y / window.innerHeight) * 100 : 33;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
        {/* Ripple container - at task origin position */}
        <div 
          className="absolute -translate-x-1/2 -translate-y-1/2" 
          style={{ left: `${originX}%`, top: `${originY}%` }}
        >
          
          {/* Outer Glow - soft halo */}
          <motion.div
            className="absolute -inset-16 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(245, 130, 39, 0.12) 0%, transparent 55%)',
            }}
            initial={{ scale: 0.2, opacity: 0 }}
            animate={{ scale: 4.4, opacity: [0, 0.6, 0] }}
            transition={{ 
              duration: 0.7,
              ease: [0.2, 0.1, 0.3, 1],
            }}
          />
          
          {/* Primary Ripple - slower, water drop effect, 10% larger */}
          <motion.div
            className="w-28 h-28 rounded-full"
            style={{
              border: '2px solid rgba(245, 130, 39, 0.3)',
              background: 'radial-gradient(circle, rgba(245, 130, 39, 0.08) 0%, transparent 50%)',
            }}
            initial={{ scale: 0.15, opacity: 0 }}
            animate={{ 
              scale: [0.15, 2.2, 3.3],
              opacity: [0, 0.55, 0],
            }}
            transition={{ 
              duration: 0.65,
              ease: [0.15, 0.1, 0.35, 1],
            }}
          />
          
          {/* Secondary ripple - softer, slightly delayed, 10% larger */}
          <motion.div
            className="absolute inset-0 w-28 h-28 rounded-full"
            style={{
              border: '1.5px solid rgba(245, 130, 39, 0.2)',
            }}
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ 
              scale: [0.3, 1.75, 2.75],
              opacity: [0, 0.4, 0],
            }}
            transition={{ 
              duration: 0.55,
              delay: 0.1,
              ease: [0.2, 0.1, 0.35, 1],
            }}
          />
        </div>

        {/* Particles - start from task origin, flow toward top/top-right */}
        {particles.map((particle, index) => {
          // Mix of directions: ~60% toward top-right, ~40% more straight up
          const goesTopRight = index % 5 !== 0 && index % 5 !== 3; // 60% top-right
          
          return particle.type === 'side' ? (
            // Side-squirt particles: shoot sideways then curve toward top-right
            <motion.div
              key={particle.id}
              className="absolute rounded-full"
              style={{
                width: particle.size * 1.1,
                height: particle.size * 1.1,
                left: `${originX}%`,
                top: `${originY}%`,
                backgroundColor: particle.color,
              }}
              initial={{ 
                opacity: 0, 
                scale: 0,
                x: 0,
                y: 0,
              }}
              animate={{ 
                opacity: [0, 0.7, 0.65, 0.5, 0.3, 0],
                scale: [0, 1.2, 1, 0.8, 0.5, 0.2],
                x: [
                  0, 
                  (particle.isLeft ? -100 : 100) + Math.random() * 30, // Squirt sideways
                  goesTopRight 
                    ? (100 - originX) * window.innerWidth / 100 * 0.35 + Math.random() * 30 // Gentle angle to top-right
                    : -20 + Math.random() * 40, // Straight up
                ],
                y: [
                  0,
                  -15 + Math.random() * 30,
                  -originY * window.innerHeight / 100 + 20,
                ],
              }}
              transition={{
                duration: 1.8,
                delay: particle.delay,
                ease: [0.2, 0.1, 0.3, 1],
                times: [0, 0.2, 1],
              }}
            />
          ) : (
            // Regular particles: mix of straight up and gentle top-right angle
            <motion.div
              key={particle.id}
              className="absolute rounded-full"
              style={{
                width: particle.size * 1.1,
                height: particle.size * 1.1,
                left: `${originX + (particle.startX - 50) * 0.3}%`,
                top: `${originY}%`,
                backgroundColor: particle.color,
              }}
              initial={{ 
                opacity: 0, 
                scale: 0,
              }}
              animate={{ 
                opacity: [0, 0.6, 0.55, 0.4, 0.2, 0],
                scale: [0, 1, 0.9, 0.75, 0.5, 0.2],
                x: goesTopRight 
                  ? (95 - originX) * window.innerWidth / 100 * (0.2 + Math.random() * 0.2) // Gentle angle toward top-right
                  : -30 + Math.random() * 60, // Mostly straight up with slight spread
                y: -originY * window.innerHeight / 100 + 30 + Math.random() * 20,
              }}
              transition={{
                duration: 1.6,
                delay: particle.delay,
                ease: [0.1, 0.15, 0.25, 1],
              }}
            />
          );
        })}
      </div>
    </AnimatePresence>
  );
};

/**
 * Card settle effect - subtle downward ease when completing
 */
export const CardSettleEffect = ({ children, isSettling }) => {
  return (
    <motion.div
      animate={isSettling ? {
        y: [0, 1.5, 0],
        scale: [1, 0.997, 1],
      } : {
        y: 0,
        scale: 1,
      }}
      transition={isSettling ? {
        duration: 0.35,
        ease: [0.25, 0.1, 0.25, 1],
      } : {}}
    >
      {children}
    </motion.div>
  );
};

export default CompletionCelebration;
